import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { TenantThemeProvider } from "@/contexts/TenantContext";
import { PlanProvider } from "@/contexts/PlanContext";
import SubscriptionGate from "@/components/SubscriptionGate";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import PendingApproval from "@/pages/PendingApproval";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import BuyerDashboard from "@/pages/BuyerDashboard";
import OrgDashboard from "@/pages/OrgDashboard";
import SalesDashboard from "@/pages/SalesDashboard";
import DealerDashboard from "@/pages/DealerDashboard";
import DistributorDashboard from "@/pages/DistributorDashboard";
import WarehouseDashboard from "@/pages/WarehouseDashboard";
import OrganizationPortal from "@/pages/portals/OrganizationPortal";
import DistributorPortal from "@/pages/portals/DistributorPortal";
import VendorPortal from "@/pages/portals/VendorPortal";
import TenantRoleLogin from "@/pages/TenantRoleLogin";
import TenantRoleWorkspace from "@/pages/TenantRoleWorkspace";
import SubscribeSuccess from "@/pages/SubscribeSuccess";
import SharePreview from "@/pages/SharePreview";
import SubscriptionPage from "@/pages/SubscriptionPage";
import RegisterPage from "@/pages/RegisterPage";
import ScrollToTop from "@/components/ScrollToTop";
import { Toaster } from "@/components/ui/sonner";

const ORG_ROLES = [
  "ORG_ADMIN", "PROCUREMENT_MANAGER", "PURCHASE_MANAGER", "PURCHASE_EXECUTIVE",
  "FINANCE_MANAGER", "ACCOUNTS_EXECUTIVE",
  "INVENTORY_MANAGER", "STORE_MANAGER", "WAREHOUSE_MANAGER",
  "PRODUCTION_MANAGER", "QC_MANAGER",
  "SALES_MANAGER", "SALES_MARKETING_MANAGER",
  "DISPATCH_MANAGER", "DISPATCH_EXECUTIVE",
  "HR_MANAGER", "CA",
];
const SALES_ROLES = ["SALES_MANAGER", "SALES_EXECUTIVE", "SALES_COORDINATOR"];
const DEALER_ROLES = ["DEALER"];
const DISTRIBUTOR_ROLES = ["DISTRIBUTOR"];
const WAREHOUSE_ROLES = ["WAREHOUSE_MANAGER", "WAREHOUSE_EXECUTIVE"];

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Public cinematic landing for unauthenticated visitors
  if (!user) return <Landing />;
  // Authenticated users go straight to their portal
  if (user.role === "SUPER_ADMIN") return <Navigate to="/admin" replace />;
  if (user.account_status !== "APPROVED") return <Navigate to="/pending-approval" replace />;
  if (user.role === "BUYER") return <Navigate to="/buyer" replace />;
  if (user.role === "DEALER") return <Navigate to="/dealer" replace />;
  if (user.role === "DISTRIBUTOR") return <Navigate to="/distributor" replace />;
  if (WAREHOUSE_ROLES.includes(user.role)) return <Navigate to="/warehouse" replace />;
  if (SALES_ROLES.includes(user.role)) return <Navigate to="/sales" replace />;
  if (ORG_ROLES.includes(user.role)) return <Navigate to="/org" replace />;
  return <Navigate to="/vendor" replace />;
}

function TenantPortal() {
  return (
    <Routes>
      <Route index element={<Login />} />
      <Route path="login" element={<Login />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TenantThemeProvider>
            <PlanProvider>
              <ScrollToTop />
              <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/login" element={<Login />} />
              <Route path="/super" element={<Login hideDemo accentLabel="Privileged access · platform console" />} />
              <Route path="/pending-approval" element={<PendingApproval />} />

              {/* Public portal landing pages */}
              <Route path="/portals/organization" element={<OrganizationPortal />} />
              <Route path="/portals/distributor" element={<DistributorPortal />} />
              <Route path="/portals/vendor" element={<VendorPortal />} />
              <Route path="/portals/:portal/subscribe/success" element={<SubscribeSuccess />} />

              {/* Public share landing (short-code) */}
              <Route path="/s/:code" element={<SharePreview />} />

              {/* Public subscription + registration */}
              <Route path="/subscribe" element={<SubscriptionPage />} />
              <Route path="/register" element={<RegisterPage />} />

              {/* Tenant-scoped role URLs: /<tenant-slug>/<role-slug>/login → /<tenant-slug>/<role-slug>/* */}
              <Route path="/:tenantSlug/:roleSlug/login" element={<TenantRoleLogin />} />
              <Route path="/:tenantSlug/:roleSlug/*" element={<TenantRoleWorkspace />} />

              <Route path="/t/:slug/*" element={<TenantPortal />} />

              <Route path="/admin/*" element={
                <ProtectedRoute roles={["SUPER_ADMIN"]}><SuperAdminDashboard /></ProtectedRoute>
              } />
              <Route path="/vendor/*" element={
                <ProtectedRoute roles={["VENDOR_ADMIN", "VENDOR_STAFF"]}><VendorDashboard /></ProtectedRoute>
              } />
              <Route path="/buyer/*" element={
                <ProtectedRoute roles={["BUYER"]}><BuyerDashboard /></ProtectedRoute>
              } />
              <Route path="/org/*" element={
                <ProtectedRoute roles={ORG_ROLES}><SubscriptionGate><OrgDashboard /></SubscriptionGate></ProtectedRoute>
              } />
              <Route path="/sales/*" element={
                <ProtectedRoute roles={SALES_ROLES}><SubscriptionGate><SalesDashboard /></SubscriptionGate></ProtectedRoute>
              } />
              <Route path="/dealer/*" element={
                <ProtectedRoute roles={DEALER_ROLES}><DealerDashboard /></ProtectedRoute>
              } />
              <Route path="/distributor/*" element={
                <ProtectedRoute roles={DISTRIBUTOR_ROLES}><DistributorDashboard /></ProtectedRoute>
              } />
              <Route path="/warehouse/*" element={
                <ProtectedRoute roles={WAREHOUSE_ROLES}><SubscriptionGate><WarehouseDashboard /></SubscriptionGate></ProtectedRoute>
              } />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster richColors position="top-right" />
            </PlanProvider>
          </TenantThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
